import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  SHOW_ONLY_ENGINE_HOUR_SUMMARY_CUSTOMER_CODES,
  SHOW_ONLY_4DAY_ROTATION_CUSTOMER_CODES,
  SHOW_ONLY_SHIFT_WISE_CUSTOMER_CODES,
  EQUIPMENT_STATUS_CUSTOMER_CODES,
  TRIPS_REPORT_CUSTOMER_CODES,
  TRIP_REPORT_SENSOR_CUSTOMER_CODES,
  MATERIAL_MOVEMENT_REPORT_CUSTOMER_CODES,
  MATERIAL_MOVEMENT_BY_DUMP_CUSTOMER_CODES,
  isReportEnabledForCustomer,
} from 'config/reportVisibility';
import EquipmentStatusReport from './components/EquipmentStatusReport';
import TripsReport from './components/TripsReport';
import TripReportSensor from './components/TripReportSensor';
import MaterialMovementReport from './components/MaterialMovementReport';
import MaterialMovementByDump from './components/MaterialMovementByDump';
import ReportAPI, { type CrewHaulageExportPayload } from 'api/report.api';
import DriverBehavorHandler from 'handlers/driver.behavior.handler';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { formatStartEndDate } from 'views/EngineHours/utils/utils';
import useStartEndDates from 'views/EngineHours/hooks/useStartEndDates';
import ShareReportModal from './componenets/ShareReportModal';
import { showNotification } from 'store/notification.slice';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

type ReportType =
  | 'shift'
  | 'rotation'
  | 'engine_hour_summary'
  | 'equipment_status'
  | 'trips_report'
  | 'trip_report_sensor'
  | 'material_movement_report'
  | 'material_movement_by_dump';
type CrewReportType = 'shift' | 'rotation' | 'engine_hour_summary';
type Crew = 'All' | 'A' | 'B' | 'C';
type Shift = 'All' | 'Day' | 'Night';

interface ExportBasePayload {
  customerId: string;
  reportType: CrewReportType;
  crewName?: string;
  shiftNumber?: number;
  shiftDate?: string;
  fromDate?: string;
  toDate?: string;
}

const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ScheduleReport = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const reportsAPI = new ReportAPI();
  const driverBehaviorHandler = new DriverBehavorHandler();
  const dispatch = useAppDispatch();
  const { customerTz, customerCode } = useCustomerInfo();
  const { preferredTimeDisplayMode, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();

  interface ReportFormData {
    reportType: ReportType;
    fromDate: string;
    toDate: string;
    crew: Crew;
    shift: Shift;
    shareReportModalOpen: boolean;
    previewUrl: string | null;
    isPreviewVisible: boolean;
    isPreviewLoading: boolean;
    isDownloading: boolean;
  }

  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }, []);

  const [formData, setFormData] = useState<ReportFormData>({
    reportType: 'shift',
    fromDate: yesterday,
    toDate: yesterday,
    crew: 'A',
    shift: 'Day',
    shareReportModalOpen: false,
    previewUrl: null,
    isPreviewVisible: false,
    isPreviewLoading: false,
    isDownloading: false,
  });
  useEffect(() => {
    if (!customerCode) return;

    const enabledReports = ['shift', 'rotation', 'engine_hour_summary'].filter(
      (r) => isReportEnabledForCustomer(customerCode, r as CrewReportType)
    );

    if (enabledReports.length > 0) {
      const defaultReport = enabledReports[0] as CrewReportType;
      setFormData((prev) => ({ ...prev, reportType: defaultReport }));
      return;
    }

    // For customers without a crew report enabled (e.g. car/ggn), open
    // straight into Trip Report (Sensor) instead of the generic shift-wise
    // filters panel, since it's not actually usable for them.
    if (TRIP_REPORT_SENSOR_CUSTOMER_CODES.has(customerCode)) {
      setFormData((prev) => ({ ...prev, reportType: 'trip_report_sensor' }));
    }
  }, [customerCode]);

  const reportName =
    formData.reportType === 'shift'
      ? 'Shift-wise Report'
      : formData.reportType === 'rotation'
      ? '4-Day Rotation Report'
      : 'Activity Tracker (Excel)';
  const reportId =
    formData.reportType === 'shift'
      ? 'shift-wise'
      : formData.reportType === 'rotation'
      ? '4-day-rotation'
      : 'engine-hour-summary';

  const customerTimezone =
    timezoneBasedOnDisplayMode ?? customerTz ?? dayjs.tz.guess();

  const isEngineHourSummary = formData.reportType === 'engine_hour_summary';

  const startDateObj = formData.fromDate
    ? isEngineHourSummary
      ? dayjs
          .tz(formData.fromDate, 'YYYY-MM-DD', customerTimezone)
          .startOf('day')
          .toDate()
      : dayjs(formData.fromDate).startOf('day').toDate()
    : new Date();
  const endDateObj = formData.toDate
    ? isEngineHourSummary
      ? dayjs
          .tz(formData.toDate, 'YYYY-MM-DD', customerTimezone)
          .startOf('day')
          .toDate()
      : dayjs(formData.toDate).startOf('day').toDate()
    : new Date();

  const formattedDates = formatStartEndDate('Custom', startDateObj, endDateObj);
  const formattedStartDate = formattedDates?.startApiDate ?? startDateObj;
  const formattedEndDate = formattedDates?.endApiDate ?? endDateObj;

  const { startDateWithTime, endDateWithTime } = useStartEndDates({
    showShiftWise: false,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  });

  const inputOutlineSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: palette.borderGrey,
      },
      '&:hover fieldset': {
        borderColor: palette.borderGrey,
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderGrey,
        borderWidth: 1.5,
      },
      '& .MuiInputBase-input': {
        color: 'text.primary',
      },
      '& .MuiSvgIcon-root': {
        color: 'text.primary',
      },
      '& .MuiInputBase-input::-webkit-calendar-picker-indicator': {
        cursor: 'pointer',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'text.primary',
      },
    },
    '& .MuiInputBase-root': {
      height: 40,
    },
  } as const;

  const resolveShiftNumber = (currentShift: Shift): number => {
    if (currentShift === 'Day') return 1;
    if (currentShift === 'Night') return 2;
    return 0;
  };

  const getRotationStartDate = (endDate: string): string =>
    dayjs(endDate).subtract(3, 'day').format('YYYY-MM-DD');

  const isReportTypeLocked =
    formData.isPreviewLoading || formData.isDownloading;

  const buildExportBasePayload = (): ExportBasePayload => {
    if (!customerId) {
      throw new Error('Customer is not selected.');
    }

    if (
      formData.reportType === 'equipment_status' ||
      formData.reportType === 'trips_report' ||
      formData.reportType === 'trip_report_sensor' ||
      formData.reportType === 'material_movement_report' ||
      formData.reportType === 'material_movement_by_dump'
    ) {
      throw new Error('This report uses its own download.');
    }

    if (formData.reportType === 'engine_hour_summary') {
      if (!formData.fromDate || !formData.toDate) {
        throw new Error(
          'Please select a From and To date for the engine hour summary report.'
        );
      }

      if (!startDateWithTime || !endDateWithTime) {
        throw new Error('Date conversion failed. Please try again.');
      }

      return {
        customerId,
        reportType: formData.reportType,
        fromDate: startDateWithTime,
        toDate: endDateWithTime,
      };
    }

    // For shift/rotation reports validate crew and shift
    if (formData.crew === 'All') {
      throw new Error('Please select a specific crew.');
    }

    if (formData.shift === 'All') {
      throw new Error('Please select a specific shift.');
    }

    const shiftNumber = resolveShiftNumber(formData.shift);
    if (shiftNumber === 0) {
      throw new Error('Invalid shift selected.');
    }

    if (formData.reportType === 'shift') {
      if (!formData.fromDate) {
        throw new Error('Please select a date for the shift-wise report.');
      }

      return {
        customerId,
        reportType: formData.reportType,
        crewName: formData.crew,
        shiftNumber,
        shiftDate: formData.fromDate,
      };
    }

    // rotation
    if (!formData.fromDate || !formData.toDate) {
      throw new Error('Please select a date for the rotation report.');
    }

    return {
      customerId,
      reportType: formData.reportType as CrewReportType,
      crewName: formData.crew,
      shiftNumber,
      fromDate: formData.fromDate,
      toDate: formData.toDate,
    };
  };

  const handleDownloadReport = async () => {
    let basePayload: ExportBasePayload;
    try {
      basePayload = buildExportBasePayload();
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          message: (error as Error).message,
          type: 'error',
        })
      );
      return;
    }

    setFormData((prev) => ({ ...prev, isDownloading: true }));
    try {
      if (formData.reportType === 'engine_hour_summary') {
        if (!basePayload.fromDate || !basePayload.toDate) {
          throw new Error('Date conversion failed. Please try again.');
        }
        const arrayBuffer =
          await driverBehaviorHandler.downloadEngineHoursTimeline(
            customerId,
            basePayload.fromDate,
            basePayload.toDate,
            preferredTimeDisplayMode,
            timezoneBasedOnDisplayMode ?? customerTz ?? dayjs.tz.guess(),
            'excel',
            false
          );
        const blob = new Blob([arrayBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        downloadFile(blob, 'engine-hour-summary.xlsx');
      } else {
        const fileType = 'pdf';
        const payload: CrewHaulageExportPayload = {
          ...basePayload,
          action: 'download',
          fileType,
        };
        const blob = await reportsAPI.exportCrewHaulageFile(payload);
        const filename =
          formData.reportType === 'shift'
            ? 'shift-wise-report.pdf'
            : 'rotation-report.pdf';
        downloadFile(blob, filename);
      }
    } catch (error) {
      const message = await ReportAPI.extractErrorMessage(error);
      console.error('Error downloading report:', error);
      dispatch(
        showNotification({
          message,
          type: 'error',
        })
      );
    } finally {
      setFormData((prev) => ({ ...prev, isDownloading: false }));
    }
  };

  const handlePreviewReport = async () => {
    let basePayload: ExportBasePayload;
    try {
      basePayload = buildExportBasePayload();
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          message: (error as Error).message,
          type: 'error',
        })
      );
      return;
    }

    const fileType =
      formData.reportType === 'engine_hour_summary' ? 'xlsx' : 'pdf';
    const payload: CrewHaulageExportPayload = {
      ...basePayload,
      action: 'download',
      fileType,
    };

    setFormData((prev) => ({ ...prev, isPreviewLoading: true }));
    try {
      const blob = await reportsAPI.exportCrewHaulageFile(payload);
      const url = window.URL.createObjectURL(blob);
      setFormData((prev) => {
        if (prev.previewUrl) {
          window.URL.revokeObjectURL(prev.previewUrl);
        }
        return {
          ...prev,
          previewUrl: url,
          isPreviewVisible: true,
        };
      });
    } catch (error) {
      const message = await ReportAPI.extractErrorMessage(error);
      console.error('Error generating preview:', error);
      dispatch(
        showNotification({
          message,
          type: 'error',
        })
      );
    } finally {
      setFormData((prev) => ({ ...prev, isPreviewLoading: false }));
    }
  };

  const handleShareByEmail = () => {
    setFormData((prev) => ({ ...prev, shareReportModalOpen: true }));
  };

  const exportBasePayload = useMemo(() => {
    try {
      return buildExportBasePayload();
    } catch {
      return null;
    }
  }, [
    customerId,
    formData.reportType,
    formData.crew,
    formData.shift,
    formData.fromDate,
    formData.toDate,
    startDateWithTime,
    endDateWithTime,
  ]);

  useEffect(() => {
    return () => {
      if (formData.previewUrl) {
        window.URL.revokeObjectURL(formData.previewUrl);
      }
    };
  }, [formData.previewUrl]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, isPreviewVisible: false }));
  }, [
    formData.reportType,
    formData.fromDate,
    formData.toDate,
    formData.crew,
    formData.shift,
  ]);

  return (
    <CustomMuiThemeProvider>
      <Box
        sx={{
          minHeight: '100%',
          color: 'text.primary',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {isLoading ? (
          <SingleFadeLoader />
        ) : (
          <>
            <Stack spacing={4}>
              {/* Heading - free-floating */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                  }}
                >
                  {t('reports')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('create_amp_download_operational_reports')}
                </Typography>
              </Box>

              {/* Main content: left = report type, right = filters + preview + actions */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                alignItems="flex-start"
              >
                {/* Report type (left column) */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    width: { xs: '100%', md: '20%' },
                    flexBasis: { md: '20%' },
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
                  >
                    {t('report_type')}
                  </Typography>
                  <Stack spacing={2}>
                    {SHOW_ONLY_SHIFT_WISE_CUSTOMER_CODES.has(customerCode) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          const today = dayjs().format('YYYY-MM-DD');
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'shift',
                            fromDate: today,
                            toDate: today,
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'shift'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'shift'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('shift_wise_report')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('generate_report_by_day_amp_night_shifts')}
                        </Typography>
                      </Paper>
                    )}

                    {SHOW_ONLY_4DAY_ROTATION_CUSTOMER_CODES.has(
                      customerCode
                    ) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'rotation',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'rotation'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'rotation'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('4_day_rotation_report')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('view_performance_across_4_day_crew_rotations')}
                        </Typography>
                      </Paper>
                    )}
                    {TRIP_REPORT_SENSOR_CUSTOMER_CODES.has(customerCode) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'trip_report_sensor',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'trip_report_sensor'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'trip_report_sensor'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('trip_report_sensor_title')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('trip_report_subtitle')}
                        </Typography>
                      </Paper>
                    )}
                    {MATERIAL_MOVEMENT_REPORT_CUSTOMER_CODES.has(
                      customerCode
                    ) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'material_movement_report',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'material_movement_report'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'material_movement_report'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('material_movement_report_title')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('material_movement_report_subtitle')}
                        </Typography>
                      </Paper>
                    )}
                    {MATERIAL_MOVEMENT_BY_DUMP_CUSTOMER_CODES.has(
                      customerCode
                    ) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'material_movement_by_dump',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'material_movement_by_dump'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'material_movement_by_dump'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('material_movement_by_dump_title')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('material_movement_by_dump_subtitle')}
                        </Typography>
                      </Paper>
                    )}
                    {TRIPS_REPORT_CUSTOMER_CODES.has(customerCode) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'trips_report',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'trips_report'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'trips_report'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('trips_report_title')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('trip_report_subtitle')}
                        </Typography>
                      </Paper>
                    )}
                    {EQUIPMENT_STATUS_CUSTOMER_CODES.has(customerCode) && (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (isReportTypeLocked) return;
                          setFormData((prev) => ({
                            ...prev,
                            reportType: 'equipment_status',
                          }));
                        }}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          opacity: isReportTypeLocked ? 0.6 : 1,
                          pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                          borderRadius: 1,
                          border: `1px solid ${
                            formData.reportType === 'equipment_status'
                              ? palette.borderGrey
                              : 'divider'
                          }`,
                          bgcolor:
                            formData.reportType === 'equipment_status'
                              ? `${palette.primary}2E`
                              : 'transparent',
                          transition: 'all 120ms ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {t('equipment_status_report')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('view_and_edit_equipment_status_per_shift')}
                        </Typography>
                      </Paper>
                    )}
                    <Paper
                      elevation={0}
                      onClick={() => {
                        if (isReportTypeLocked) return;
                        setFormData((prev) => ({
                          ...prev,
                          reportType: 'engine_hour_summary',
                        }));
                      }}
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        opacity: isReportTypeLocked ? 0.6 : 1,
                        pointerEvents: isReportTypeLocked ? 'none' : 'auto',
                        borderRadius: 1,
                        border: `1px solid ${
                          formData.reportType === 'engine_hour_summary'
                            ? palette.borderGrey
                            : 'divider'
                        }`,
                        bgcolor:
                          formData.reportType === 'engine_hour_summary'
                            ? `${palette.primary}2E`
                            : 'transparent',
                        transition: 'all 120ms ease',
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                      >
                        {t('engine_hour_summary_excel')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {t(
                          'export_engine_hours_summary_as_excel_for_a_date_range'
                        )}
                      </Typography>
                    </Paper>
                  </Stack>
                </Paper>

                {/* Filters + preview + actions (right column) */}
                {formData.reportType === 'trips_report' ? (
                  <TripsReport />
                ) : formData.reportType === 'trip_report_sensor' ? (
                  <TripReportSensor />
                ) : formData.reportType === 'material_movement_report' ? (
                  <MaterialMovementReport />
                ) : formData.reportType === 'material_movement_by_dump' ? (
                  <MaterialMovementByDump />
                ) : formData.reportType === 'equipment_status' ? (
                  <EquipmentStatusReport />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'background.paper',
                      width: { xs: '100%', md: '80%' },
                      flexBasis: { md: '80%' },
                      flexGrow: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
                    >
                      {t('filters_word')}
                    </Typography>

                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 1.5,
                          }}
                        >
                          {formData.reportType === 'rotation'
                            ? t('date')
                            : t('date_range')}
                        </Typography>
                        {formData.reportType === 'rotation' ? (
                          <DatePicker
                            label={t('date')}
                            value={
                              formData.toDate ? dayjs(formData.toDate) : null
                            }
                            maxDate={dayjs()}
                            onChange={(newValue) => {
                              const formattedValue =
                                newValue?.format('YYYY-MM-DD') ?? '';
                              setFormData((prev) => ({
                                ...prev,
                                toDate: formattedValue,
                                fromDate: formattedValue
                                  ? getRotationStartDate(formattedValue)
                                  : '',
                              }));
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                sx: inputOutlineSx,
                              },
                            }}
                          />
                        ) : (
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <DatePicker
                              label={t('from')}
                              value={
                                formData.fromDate
                                  ? dayjs(formData.fromDate)
                                  : null
                              }
                              maxDate={dayjs()}
                              onChange={(newValue) => {
                                const formattedValue =
                                  newValue?.format('YYYY-MM-DD') ?? '';
                                setFormData((prev) => {
                                  if (prev.reportType === 'shift') {
                                    return {
                                      ...prev,
                                      fromDate: formattedValue,
                                      toDate: formattedValue,
                                    };
                                  }

                                  return {
                                    ...prev,
                                    fromDate: formattedValue,
                                  };
                                });
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  sx: inputOutlineSx,
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              {t('x2192')}
                            </Typography>
                            <DatePicker
                              label={t('to')}
                              value={
                                formData.toDate ? dayjs(formData.toDate) : null
                              }
                              maxDate={dayjs()}
                              minDate={
                                formData.fromDate
                                  ? dayjs(formData.fromDate)
                                  : undefined
                              }
                              onChange={(newValue) => {
                                const formattedValue =
                                  newValue?.format('YYYY-MM-DD') ?? '';
                                setFormData((prev) => {
                                  if (prev.reportType === 'shift') {
                                    return {
                                      ...prev,
                                      toDate: formattedValue,
                                      fromDate: formattedValue,
                                    };
                                  }

                                  return {
                                    ...prev,
                                    toDate: formattedValue,
                                  };
                                });
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  sx: inputOutlineSx,
                                },
                              }}
                            />
                          </Stack>
                        )}
                      </Grid>

                      {formData.reportType !== 'engine_hour_summary' && (
                        <>
                          <Grid item xs={12} md={3}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 1.5,
                              }}
                            >
                              {t('shift_word')}
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              sx={inputOutlineSx}
                            >
                              <Select
                                labelId="shift-label"
                                value={formData.shift || 'Day'}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    shift: e.target.value as Shift,
                                  }));
                                }}
                                displayEmpty={false}
                              >
                                <MenuItem value="Day">
                                  {t('shift_day')}
                                </MenuItem>
                                <MenuItem value="Night">
                                  {t('shift_night')}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 1.5,
                              }}
                            >
                              {t('crew_word')}
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              sx={inputOutlineSx}
                            >
                              <Select
                                labelId="crew-label"
                                value={formData.crew || 'A'}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    crew: e.target.value as Crew,
                                  }));
                                }}
                                displayEmpty={false}
                              >
                                <MenuItem value="A">{t('crew_a')}</MenuItem>
                                <MenuItem value="B">{t('crew_b')}</MenuItem>
                                <MenuItem value="C">{t('crew_c')}</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </Grid>

                    {/* Preview (PDF) - only shown after clicking Preview */}
                    {(formData.isPreviewVisible ||
                      formData.isPreviewLoading) && (
                      <Box sx={{ mt: 4 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              display: 'flex',
                              gap: 1,
                            }}
                          >
                            {t('report_preview')}
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            borderRadius: 1,
                            overflow: 'hidden',
                            height: 700,
                          }}
                        >
                          {formData.isPreviewLoading ? (
                            <Box
                              sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {t('generating_preview')}
                              </Typography>
                            </Box>
                          ) : formData.previewUrl ? (
                            <iframe
                              title={t('report_preview_2')}
                              src={formData.previewUrl}
                              style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {t('preview_is_not_available_please_try_again')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Actions */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 4 }}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={handlePreviewReport}
                        disabled={
                          formData.isPreviewLoading ||
                          formData.isDownloading ||
                          formData.reportType === 'engine_hour_summary'
                        }
                        sx={{
                          textTransform: 'none',
                          borderColor: palette.borderGrey,
                          color: 'text.primary',
                          '&:hover': {
                            borderColor: palette.borderGrey,
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {formData.isPreviewLoading
                          ? t('previewing_loading')
                          : t('preview')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={
                          formData.isPreviewLoading || formData.isDownloading
                        }
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadReport}
                        sx={{ textTransform: 'none' }}
                      >
                        {formData.isDownloading
                          ? t('downloading_loading')
                          : t('download_report')}
                      </Button>
                      {formData.reportType !== 'engine_hour_summary' && (
                        <Button
                          variant="outlined"
                          startIcon={<ShareIcon />}
                          onClick={handleShareByEmail}
                          disabled={
                            formData.isPreviewLoading || formData.isDownloading
                          }
                          sx={{
                            textTransform: 'none',
                            borderColor: palette.borderGrey,
                            color: 'text.primary',
                            '&:hover': {
                              borderColor: palette.borderGrey,
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {t('share_word')}
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Stack>

            {formData.shareReportModalOpen && (
              <ShareReportModal
                open={formData.shareReportModalOpen}
                reportId={reportId}
                reportName={reportName}
                basePayload={exportBasePayload}
                handleAbort={() => {
                  setFormData((prev) => ({
                    ...prev,
                    shareReportModalOpen: false,
                  }));
                }}
              />
            )}
          </>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};

export default ScheduleReport;
